import { create } from 'zustand'
import {
  getMutuallyExclusivePartner,
  getPreservedParamsForCollection,
} from '../constants'
import type {
  CollectionFilterStateV2,
  FilterActionsSelector,
  FilterStateSelector,
  FilterValues,
} from '../types/filter-store-v2.types'
import { ConcatSlugHelper } from '../utils/concat-slug.helper'
import { findMatchingFacetValueWithPriority } from '../utils/facet-matching'
import { urlBuilder } from '../utils/url-builder.service'

// ==================== STORE ====================

export const useCollectionFilterStoreV2 = create<CollectionFilterStateV2>(
  (set, get) => ({
    // Initial state
    filters: {},
    originalSlug: undefined,
    concatValue: null,
    isConcatLocked: false,
    isInitialized: false,
    facets: undefined,
    cardFacets: undefined,

    // Private method to sync URL with current state
    syncUrlWithState: () => {
      const state = get()
      if (!state.originalSlug || typeof window === 'undefined') return

      const preservedParams = getPreservedParamsForCollection(
        state.originalSlug
      )

      const url = urlBuilder.buildCollectionUrl({
        originalSlug: state.originalSlug,
        filters: state.filters,
        concatValue: state.concatValue,
        currentPathname: window.location.pathname,
        facets: state.facets,
        cardFacets: state.cardFacets,
        preserveParams:
          preservedParams.length > 0 ? preservedParams : undefined,
      })

      urlBuilder.updateBrowserUrl(url)
    },

    // Initialize store với originalSlug và initial filters (+ map concat ngay tại đây để tránh giật UI)
    initialize: (
      originalSlug,
      initialFilters = {},
      facets?: import('@/types/collection').TFacet[],
      cardFacets?: import('@/types/collection').TCardFacet[]
    ) => {
      const currentState = get()

      // Check if navigating to different collection
      const isCollectionChanged =
        currentState.originalSlug && currentState.originalSlug !== originalSlug

      // Extract and normalize concat value using ConcatSlugHelper
      let concatValue: string | null = null
      const mappedFilters: FilterValues = { ...initialFilters }

      if (typeof window !== 'undefined' && (facets || cardFacets)) {
        const { raw, normalized } = ConcatSlugHelper.extractAndNormalize(
          window.location.pathname,
          originalSlug,
          facets || [],
          cardFacets || []
        )

        concatValue = normalized

        // If we have a normalized concat value, map it to the appropriate facet
        // Use priority helper to ensure color is prioritized
        if (normalized && raw) {
          const match = findMatchingFacetValueWithPriority(
            normalized,
            facets || [],
            cardFacets || []
          )
          if (match) {
            mappedFilters[match.facet.seo_alias] = match.value.seo_alias
          }
        }
      }

      // Determine concat lock state:
      // - If collection changed → unlock (false) to allow new concat
      // - Otherwise, lock if concat value exists
      const shouldLockConcat = isCollectionChanged
        ? false
        : concatValue !== null

      set({
        originalSlug,
        filters: mappedFilters,
        concatValue,
        isConcatLocked: shouldLockConcat,
        isInitialized: true,
        facets,
        cardFacets,
      })
    },

    // Set concat value với immediate URL update
    // Implements sticky concat logic: once set, cannot be changed until cleared
    setConcatValue: (value) => {
      const currentState = get()

      // If trying to set a NEW concat value (not null) but concat is already locked
      // → REJECT the change (sticky behavior)
      if (
        value !== null &&
        currentState.isConcatLocked &&
        currentState.concatValue !== null &&
        currentState.concatValue !== value
      ) {
        console.warn(
          `[Concat Lock] Cannot change concat value from "${currentState.concatValue}" to "${value}". Clear the existing filter first.`
        )
        return
      }

      // Allow clearing concat (value = null) → unlock
      if (value === null) {
        set({ concatValue: null, isConcatLocked: false })
      } else {
        // Set new concat value and lock it
        set({ concatValue: value, isConcatLocked: true })
      }

      get().syncUrlWithState()
    },

    // Set toàn bộ filters
    setFilters: (filters) => {
      set({ filters })
    },

    // Set một filter cụ thể với immediate URL update
    setFilter: (key, value) => {
      // Update store state first
      set((state) => {
        const newFilters = { ...state.filters }
        let newConcatValue = state.concatValue
        let newConcatLocked = state.isConcatLocked

        // Find if this filter is a concat filter
        const allFacets = [...(state.facets || []), ...(state.cardFacets || [])]
        const isConcatFilter = allFacets.some(
          (f) => f?.seo_alias === key && f?.is_concat_slug
        )

        if (value === undefined || value === null || value === '') {
          // Deleting/clearing filter completely
          delete newFilters[key]

          // If this was the concat filter, clear concat value
          if (isConcatFilter && state.concatValue) {
            newConcatValue = null
            newConcatLocked = false
          }
        } else if (Array.isArray(value) && value.length === 0) {
          // Deleting filter (empty array)
          delete newFilters[key]

          // If this was the concat filter, clear concat value
          if (isConcatFilter && state.concatValue) {
            newConcatValue = null
            newConcatLocked = false
          }
        } else {
          // Setting new value(s)
          newFilters[key] = value

          // Check for mutually exclusive partner facet and clear it
          // This should happen before concat logic to ensure proper state management
          const mutuallyExclusivePartner = getMutuallyExclusivePartner(key)
          if (
            mutuallyExclusivePartner &&
            newFilters[mutuallyExclusivePartner]
          ) {
            // Clear all values in the partner facet
            delete newFilters[mutuallyExclusivePartner]

            // If the partner was a concat filter, also clear its concat value
            const partnerIsConcatFilter = allFacets.some(
              (f) =>
                f?.seo_alias === mutuallyExclusivePartner && f?.is_concat_slug
            )
            if (partnerIsConcatFilter && state.concatValue) {
              // Check if the current concat value belongs to the partner facet
              const partnerFacet = allFacets.find(
                (f) => f?.seo_alias === mutuallyExclusivePartner
              )
              if (partnerFacet) {
                const partnerValues = partnerFacet.facet_values || []
                const concatValueMatchesPartner = partnerValues.some(
                  (v) => v.seo_alias === state.concatValue
                )
                if (concatValueMatchesPartner) {
                  newConcatValue = null
                  newConcatLocked = false
                }
              }
            }
          }

          // Clear gender_type filter when danh-muc or sub-danh-muc is selected
          if (
            (key === 'danh-muc' || key === 'sub-danh-muc') &&
            newFilters.gender_type
          ) {
            delete newFilters.gender_type
          }

          // If this is a concat filter, handle concat logic
          if (isConcatFilter) {
            // Find the facet to check is_allow_multiple
            const facet = allFacets.find((f) => f?.seo_alias === key)
            const isAllowMultiple = facet?.is_allow_multiple || false

            // Convert to array for easier processing
            const values = Array.isArray(value) ? value : [String(value)]

            if (values.length === 1) {
              // Single value: Set as concat value (nối vào slug)
              newConcatValue = values[0]
              newConcatLocked = true
            } else if (values.length > 1) {
              // Multiple values
              if (isAllowMultiple) {
                // Facet allows multiple: NO concat in slug, all values to query params
                newConcatValue = null
                newConcatLocked = false
              } else {
                // Facet doesn't allow multiple: First value in slug, rest in query params
                newConcatValue = values[0]
                newConcatLocked = true
              }
            }
          }
        }

        // Reset page khi thay đổi filter
        newFilters.page = 1

        return {
          filters: newFilters,
          concatValue: newConcatValue,
          isConcatLocked: newConcatLocked,
        }
      })

      // Immediate URL update
      get().syncUrlWithState()
    },

    // Clear tất cả filters (giữ lại sort nếu có) với immediate URL update
    clearFilters: () => {
      // Update store state first
      set((state) => {
        const newFilters: FilterValues = {}

        // Giữ lại sort nếu có
        if (state.filters.sort) {
          newFilters.sort = state.filters.sort
        }

        newFilters.page = 1
        return {
          filters: newFilters,
          concatValue: null, // Clear concat value khi clear filters
          isConcatLocked: false, // Unlock concat when clearing all filters
        }
      })

      // Immediate URL update
      get().syncUrlWithState()
    },

    // Set sort với immediate URL update
    setSort: (value) => {
      // Update store state first
      set((state) => ({
        filters: { ...state.filters, sort: value, page: 1 },
        concatValue: null, // Clear concatValue khi set sort
      }))

      // Immediate URL update
      get().syncUrlWithState()
    },

    // Set page
    setPage: (page) => {
      set((state) => ({
        filters: { ...state.filters, page },
      }))
    },

    // Reset store về initial state
    reset: () => {
      set({
        filters: {},
        originalSlug: undefined,
        concatValue: null,
        isConcatLocked: false,
        isInitialized: false,
      })
    },
  })
)

// ==================== SELECTORS ====================

// Selector để lấy chỉ filter state
export const useFilterState = (): FilterStateSelector =>
  useCollectionFilterStoreV2((state) => ({
    filters: state.filters,
    originalSlug: state.originalSlug,
    concatValue: state.concatValue,
    isConcatLocked: state.isConcatLocked,
    isInitialized: state.isInitialized,
  }))

// Selector để lấy chỉ filter actions
export const useFilterActions = (): FilterActionsSelector =>
  useCollectionFilterStoreV2((state) => ({
    setFilters: state.setFilters,
    setFilter: state.setFilter,
    clearFilters: state.clearFilters,
    setSort: state.setSort,
    setPage: state.setPage,
    initialize: state.initialize,
    setConcatValue: state.setConcatValue,
    reset: state.reset,
  }))

// Selector để lấy tất cả (state + actions)
export const useCollectionFilter = () => useCollectionFilterStoreV2()

// ==================== UTILITIES ====================

// Helper để check xem có filter nào đang active không, bỏ qua itm_
export const hasActiveFilters = (filters: FilterValues): boolean => {
  return Object.keys(filters).some(
    (key) => key !== 'page' && !key.startsWith('itm_') && filters[key] !== undefined && filters[key] !== ''
  )
}

// Helper để get filter count (không tính page)
export const getFilterCount = (filters: FilterValues): number => {
  return Object.keys(filters).filter(
    (key) => key !== 'page' && !key.startsWith('itm_') && filters[key] !== undefined && filters[key] !== ''
  ).length
}
